import React, { FC, useCallback, useId } from 'react';
import { useLingui } from '@lingui/react/macro';

import { AvatarType } from '@rover/components/src/Avatar/Avatar';
import FancyCheckbox from '@rover/components/src/formFields/FancyCheckbox';
import RowList from '@rover/components/src/RowList';
import { Box } from '@rover/kibble/core';
import Avatar from '@rover/kibble/patterns/Avatar';
import Row from '@rover/kibble/patterns/Row';
import type { Pet, PetType } from '@rover/types/src/Pet';

export type PetSelectorBodyProps = {
  pets: Pet[];
  selectedPets: string[];
  onPetSelect: (pet: Pet) => void;
};

const PetSelectorBody: FC<PetSelectorBodyProps> = ({ pets, selectedPets, onPetSelect }) => {
  const { t } = useLingui();
  const staticId = useId();

  const handlePetClick = useCallback(
    (pet: Pet) => {
      onPetSelect(pet);
    },
    [onPetSelect]
  );

  const getPetType = (petType: PetType): AvatarType => {
    switch (petType) {
      case 'dog':
      case 'puppy':
        return AvatarType.Dog;
      case 'cat':
        return AvatarType.Cat;
      default:
        return AvatarType.Other;
    }
  };

  return (
    <RowList>
      {pets.map((pet) => {
        const inputId = `${staticId}-${pet.pk}`;
        const imageUrl = pet.defaultImage?.isFallback ? null : pet.defaultImage?.bigThumb;
        const petType = getPetType(pet.petType);

        const isChecked = selectedPets.includes(pet.pk.toString());
        return (
          <Row
            key={pet.pk}
            role="button"
            ariaLabel={`${pet.name}, ${isChecked ? t`checked` : t`unchecked`}`}
            onClick={() => handlePetClick(pet)}
            width="100%"
            label={pet.name}
            leftItem={<Avatar imageUrl={imageUrl} type={petType} />}
            rightItem={
              <Box aria-hidden="true">
                <FancyCheckbox
                  labelIsProvidedViaHtmlFor
                  actualId={inputId}
                  checked={isChecked}
                  id={staticId}
                  label={pet.name}
                  hideLabel
                />
              </Box>
            }
          />
        );
      })}
    </RowList>
  );
};

export default PetSelectorBody;
